import type React from 'react';

import MessageContainer from 'components/MessageContainer';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';

import type { UserMessageComponent } from 'api/types';
import MessageMetadata from '../MessageMetadata/MessageMetadata';
import UserMessage from 'components/UserMessage';

import { selectIsAutonomousAgent } from 'reducers/config';
import { useAppSelector } from 'hooks';
import { AttachmentsMessage } from './AttachmentsMessage';
import { ArticlesMessage } from './ArticlesMessage';
import IrArticleTextMessage from 'components/IrArticleTextMessage';
import { selectIsAuthorIconVisible } from 'reducers/workflows';
import { SuncoSubmitCsatButton } from './SuncoSubmitCsatButton';
import { hasMetadata } from 'components/MessageMetadata/helpers';
import { useEffect, useState } from 'react';
import { IconLanguage } from '@tabler/icons-react';
import { Box, IconButton } from '@mui/material';
import { Tooltip } from '@forethought-technologies/forethought-elements';

interface MessageStepProps {
  isLastStep: boolean;
  stepIndex: number;
  widgetComponent: UserMessageComponent;
}

const MessageStep: React.FC<React.PropsWithChildren<MessageStepProps>> = ({
  stepIndex,
  widgetComponent,
}) => {
  const { component_type: componentType, isSent } = widgetComponent;
  const {
    agentIcon,
    agentName,
    articles = [],
    attachments = [],
    knowledge_articles: knowledgeArticles,
    message,
    metadata,
    read_article_action: readArticleAction,
    should_render_as_markdown: shouldRenderAsMarkdown,
    should_render_single_new_line_characters:
      shouldRenderSingleNewLineCharacters,
    translated_message: translatedMessage,
  } = widgetComponent.component_fields;
  const isAutonomousAgent = useAppSelector(selectIsAutonomousAgent);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );
  const isAdminOnly = componentType === 'visible_only_to_admins';
  const isUserMessage = componentType === 'user_message';

  // translation toggle
  const shouldRenderTranslationToggle = Boolean(message && translatedMessage);
  const [visibleMessage, setVisibleMessage] = useState(
    translatedMessage || message,
  );

  useEffect(() => {
    setVisibleMessage(translatedMessage || message);
  }, [translatedMessage, message]);

  const handleToggleMessage = () => {
    setVisibleMessage(prev =>
      prev === message && translatedMessage ? translatedMessage : message,
    );
  };

  if (
    (!message &&
      !articles.length &&
      !attachments.length &&
      // Salesforce One Chat's Enhanced Link has metadata but no message text:
      !hasMetadata(metadata)) ||
    readArticleAction === 'none'
  ) {
    return null;
  }

  if ((isSent && isAutonomousAgent) || isUserMessage) {
    return (
      <UserMessage
        nonMessageChildren={<AttachmentsMessage attachments={attachments} />}
        stepIndex={stepIndex}
        timestamp={widgetComponent.timestamp}
      >
        {visibleMessage}
      </UserMessage>
    );
  }

  /**
   * This MessageContent is used regardless of whether or not
   * we are rendering the translation toggle for zopim one chat
   */
  const MessageContent = shouldRenderAsMarkdown ? (
    <IrArticleTextMessage
      agentIcon={agentIcon}
      isAdminOnly={isAdminOnly}
      isAuthorIconVisible={isAuthorIconVisible}
      isLiveChat={widgetComponent.isInAgentConvo}
      message={visibleMessage}
      renderSingleNewLineCharacters={shouldRenderSingleNewLineCharacters}
      widgetComponent={widgetComponent}
    />
  ) : (
    /**
     * If `message` is a string that contains HTML, it HAS to be used here
     * directly, it can't be wrapped in another component, because
     * <AgathaMessage> expects it to be a plain string, in which case it will
     * put it in `dangerouslySetInnerHTML`
     */
    visibleMessage
  );

  /**
   * The reason for this is to support the inline translation toggle
   * for zopim one chat, we need to pass a React node which gets rid
   * of the setDangerouslySetInnerHTML in AgathaMessage
   */
  const AgathaMessageContent = shouldRenderTranslationToggle ? (
    <>
      {MessageContent}
      <Box
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
        }}
      >
        <Tooltip
          tooltipContent={
            visibleMessage === translatedMessage
              ? 'See original'
              : 'See translation'
          }
        >
          <IconButton
            onClick={handleToggleMessage}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
              },
              border: '1px solid rgba(0, 0, 0, 0.12)',
              ml: '6px',
              p: '4px',
              position: 'relative',
              top: '2px',
            }}
          >
            <IconLanguage size={16} />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  ) : (
    MessageContent
  );

  return (
    <MessageContainer
      className={metadata?.title || metadata?.description ? 'has-link' : ''}
    >
      {shouldRenderAsMarkdown && knowledgeArticles ? (
        <IrArticleTextMessage
          agentIcon={agentIcon}
          isAdminOnly={isAdminOnly}
          isAuthorIconVisible={isAuthorIconVisible}
          isLiveChat={widgetComponent.isInAgentConvo}
          message={visibleMessage}
          renderSingleNewLineCharacters={shouldRenderSingleNewLineCharacters}
          widgetComponent={widgetComponent}
        />
      ) : (
        <AgathaMessage
          agentIcon={agentIcon}
          agentName={agentName}
          hasLink={Boolean(metadata?.title || metadata?.description)}
          hideTimestamp={widgetComponent.hideTimestamp}
          isAdminOnly={isAdminOnly}
          isAuthorIconVisible={isAuthorIconVisible}
          isLiveChat={widgetComponent.isInAgentConvo}
          nonMessageChildren={
            <>
              <AttachmentsMessage attachments={attachments} />
              <ArticlesMessage articles={articles} />
              <MessageMetadata metadata={metadata} stepIndex={stepIndex} />
            </>
          }
          stepIndex={stepIndex}
          timestamp={widgetComponent.timestamp}
        >
          {AgathaMessageContent}
        </AgathaMessage>
      )}

      {widgetComponent.component_fields.csat_survey_link && (
        <SuncoSubmitCsatButton
          csatLink={widgetComponent.component_fields.csat_survey_link}
          csatText={widgetComponent.component_fields.csat_survey_text}
        />
      )}
    </MessageContainer>
  );
};

export default MessageStep;
